import { createBrowserRouter, Outlet } from 'react-router-dom';
import { ProtectedRoute, SubscriptionProtectedRoute } from './auth-loader';
import { BudgetLayout } from './budget/budget-layout';
import { DashboardLayout } from './dashboard/dashboard-layout';
import { PublicLayout } from './public/public-layout';

export const routes = [
  {
    path: '/',
    element: (
      <PublicLayout>
        <Outlet />
      </PublicLayout>
    ),
    children: [
      {
        path: '',
        lazy: async () => {
          const { HomePage } = await import('./public/home/home-page');
          return { Component: HomePage };
        },
      },
      {
        path: '/how-it-works',
        lazy: async () => {
          const { HowItWorksPage } = await import('./public/how-it-works/how-it-works-page');
          return { Component: HowItWorksPage };
        },
      },
      {
        path: '/about',
        lazy: async () => {
          const { AboutPage } = await import('./public/about/about-page');
          return { Component: AboutPage };
        },
      },
      {
        path: '/sign-up',
        lazy: async () => {
          const { SignUpPage } = await import('./public/sign-up/sign-up-page');
          return { Component: SignUpPage };
        },
      },
      {
        path: '/login',
        lazy: async () => {
          const { LoginPage } = await import('./public/login/login-page');
          return { Component: LoginPage };
        },
      },
      {
        path: '/forgot-password',
        lazy: async () => {
          const { ForgotPasswordPage } = await import('./public/forgot-password/forgot-password-page');
          return { Component: ForgotPasswordPage };
        },
      },
      {
        path: '/reset-password/:token',
        lazy: async () => {
          const { ResetPasswordPage } = await import('./public/reset-password/reset-password-page');
          return { Component: ResetPasswordPage };
        },
      },
      {
        path: '/privacy-policy',
        lazy: async () => {
          const { PrivacyPolicyPage } = await import('./public/privacy-policy/privacy-policy-page');
          return { Component: PrivacyPolicyPage };
        },
      },
      {
        path: '/terms',
        lazy: async () => {
          const { TermsPage } = await import('./public/terms/terms-page');
          return { Component: TermsPage };
        },
      },
      {
        path: '/logout',
        lazy: async () => {
          const { LogoutPage } = await import('./public/logout/logout-page');
          return { Component: LogoutPage };
        },
      },
    ],
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <DashboardLayout>
          <Outlet />
        </DashboardLayout>
      </ProtectedRoute>
    ),
    children: [
      {
        path: 'budget-manager',
        lazy: async () => {
          const { BudgetManagerPage } = await import('./dashboard/budget-manager/budget-manager-page');
          return { Component: BudgetManagerPage };
        },
      },
      {
        path: '/dashboard/tutorial',
        lazy: async () => {
          const { TutorialPage: BudgetTutorialPage } = await import('./dashboard/tutorial/tutorial-page');
          return { Component: BudgetTutorialPage };
        },
      },
      {
        path: 'account-settings',
        lazy: async () => {
          const { AccountSettingsPage } = await import('./dashboard/account-settings/account-settings-page');
          return { Component: AccountSettingsPage };
        },
      },
      {
        path: 'subscription-success',
        lazy: async () => {
          const { SubscriptionSuccessPage } = await import(
            './dashboard/subscription-success/subscription-success-page'
          );
          return { Component: SubscriptionSuccessPage };
        },
      },
      {
        path: 'admin',
        lazy: async () => {
          const { AdminPage } = await import('./dashboard/admin/admin-page');
          return { Component: AdminPage };
        },
      },
    ],
  },
  {
    path: '/budget/:budgetId/',
    element: (
      <ProtectedRoute>
        <SubscriptionProtectedRoute>
          <BudgetLayout>
            <Outlet />
          </BudgetLayout>
        </SubscriptionProtectedRoute>
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        lazy: async () => {
          const { BudgetPage } = await import('./budget/budget/budget-page');
          return { Component: BudgetPage };
        },
      },
      {
        path: 'account/:accountId?',
        lazy: async () => {
          const { BudgetAccountPage } = await import('./budget/account/budget-account-page');
          return { Component: BudgetAccountPage };
        },
      },
      {
        path: 'projections',
        lazy: async () => {
          const { BudgetProjectionsPage } = await import('./budget/projections/budget-projections-page');
          return { Component: BudgetProjectionsPage };
        },
      },
      {
        path: 'recurring-transactions',
        lazy: async () => {
          const { BudgetRecurringTransactionsPage } = await import(
            './budget/recurring-transactions/budget-recurring-transactions-page'
          );
          return { Component: BudgetRecurringTransactionsPage };
        },
      },
      {
        path: 'reports',
        lazy: async () => {
          const { BudgetReportsPage } = await import('./budget/reports/budget-reports-page');
          return { Component: BudgetReportsPage };
        },
      },
      {
        path: 'reports/:reportType',
        lazy: async () => {
          const { BudgetReportsPage } = await import('./budget/reports/budget-reports-page');
          return { Component: BudgetReportsPage };
        },
      },
    ],
  },
  {
    path: '*',
    lazy: async () => {
      const { NotFoundPage } = await import('./public/error/not-found');
      return { Component: NotFoundPage };
    },
  },
];

export const createRouter: () => ReturnType<typeof createBrowserRouter> = () => createBrowserRouter(routes);
